import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card';
import { SectionLabel } from '@/components/ui/section-label';
import type { Tool } from '@/features/traceability/type/Tool';
import type { ToolState } from '@/features/traceability/type/ToolState';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { toolState } from '@/features/traceability/data/toolState';
import { ToolReferenceEditor } from './ToolReferenceEditor';

export type ToolMetadataCardProps = {
  tool: Tool | null;
  lastTraceToolState?: ToolState | null;
  topMachine?: string | null;
  className?: string;
  /** Enables "Modifier" on the reference; called with the saved reference. */
  onReferenceChange?: (reference: string) => void;
  /** Open the reference editor on load (`?edit=reference`, from a refused creation). */
  startEditingReference?: boolean;
};

export function ToolMetadataCard({
  tool,
  lastTraceToolState,
  topMachine,
  className,
  onReferenceChange,
  startEditingReference = false,
}: ToolMetadataCardProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();
  const [isEditing, setIsEditing] = useState(startEditingReference && !!onReferenceChange);
  const [change, setChange] = useState<{ from: string; to: string; created: boolean } | null>(null);

  if (!tool) return null;

  const notProvided = t('toolView.metadata.fallback.notProvided');

  const getToolStateLabel = (stateValue: ToolState | null | undefined): string => {
    if (!stateValue?.toolState) return notProvided;
    const state = toolState.find((s) => s.value === stateValue.toolState);
    return state?.labelKey ? t(state.labelKey) : notProvided;
  };

  const referenceResolved = resolve(tool.reference ?? '');

  type Field = {
    label: string;
    value: string;
    mono?: boolean;
    secondaryValue?: string;
    editReference?: boolean;
  };

  const fields: Field[] = [
    { label: t('toolView.metadata.fields.id'), value: tool.toolId, mono: true },
    {
      label: t('toolView.metadata.fields.reference'),
      value: referenceResolved.label || notProvided,
      secondaryValue: referenceResolved.hasAlias ? referenceResolved.canonical : undefined,
      editReference: !!onReferenceChange && !isEditing,
    },
    { label: t('toolView.metadata.fields.state'), value: getToolStateLabel(lastTraceToolState) },
    ...(topMachine ? [{ label: t('toolView.metadata.fields.machine'), value: topMachine }] : []),
  ];

  const gridCols = topMachine ? 'sm:grid-cols-4' : 'sm:grid-cols-3';

  return (
    <Card as="section" tier="inset" padding="none" className={cn('px-5 py-4', className)}>
      <SectionLabel as="h3" className="mb-3">
        {t('toolView.metadata.title')}
      </SectionLabel>
      <dl className={cn('grid gap-4 text-sm', gridCols)}>
        {fields.map((field) => (
          <div key={field.label}>
            <dt className="eyebrow flex items-center gap-1">
              {field.label}
              {field.editReference && (
                <Button
                  type="button"
                  variant="link"
                  size="inline"
                  onClick={() => {
                    setChange(null);
                    setIsEditing(true);
                  }}
                  aria-label={t('toolPickers.edit.buttonLabel')}
                  className="ml-1 gap-1 text-xs normal-case tracking-normal"
                >
                  <Pencil className="size-3" aria-hidden />
                  {t('toolPickers.edit.button')}
                </Button>
              )}
            </dt>
            <dd className={cn('text-sm font-semibold text-foreground', field.mono && 'font-mono')}>
              {field.value}
            </dd>
            {field.secondaryValue && (
              <dd className="text-xs font-mono text-muted-foreground">{field.secondaryValue}</dd>
            )}
          </div>
        ))}
      </dl>
      {isEditing && onReferenceChange && (
        <div className="mt-3">
          <ToolReferenceEditor
            tool={tool}
            onCancel={() => setIsEditing(false)}
            onSaved={(reference, created) => {
              setChange({ from: tool.reference ?? '', to: reference, created });
              setIsEditing(false);
              onReferenceChange(reference);
            }}
          />
        </div>
      )}
      {change && (
        <Callout tone="success" size="inline" data-testid="reference-changed" className="mt-3">
          {t(change.created ? 'toolPickers.edit.savedNew' : 'toolPickers.edit.saved', {
            from: change.from || t('toolPickers.toolId.noReference'),
            to: change.to,
          })}
        </Callout>
      )}
    </Card>
  );
}
